// Catalog Shell - Wizard Component
// ============================================================================

import { createElement, clearElement } from '../../utils/dom';
import { createButton } from '../../utils/pane-ui';
import { createStepProgress, type StepProgress } from '../progress/step-progress';
import { DisposableStore } from '../disposable';
import type { IDisposable } from '../types';
import type { WizardConfig, WizardStep, WizardStepContext, WizardAPI } from './types';

// ============================================================================
// Wizard
// ============================================================================

export class Wizard implements WizardAPI, IDisposable {
    private config: WizardConfig;
    private state: Record<string, unknown>;
    private currentIndex = 0;
    private disposed = false;

    // DOM
    private container: HTMLElement;
    private stepIndicator: StepProgress;
    private contentEl: HTMLElement;
    private footerEl: HTMLElement;
    private errorEl: HTMLElement;
    private backBtn!: HTMLButtonElement;
    private nextBtn!: HTMLButtonElement;
    private cancelBtn: HTMLButtonElement | null = null;

    // Per-step disposables
    private stepDisposables: DisposableStore | null = null;

    constructor(container: HTMLElement, config: WizardConfig) {
        this.config = config;
        this.state = { ...(config.initialState ?? {}) };
        this.container = container;

        // Added, never assigned: a host the caller styled (a card) stays styled.
        // Assigning `className` used to strip the caller's classes, so a wizard
        // mounted on a card silently lost the card's width and its look.
        this.container.classList.add('cs-wizard');
        if (config.className) this.container.classList.add(...config.className.split(/\s+/).filter(Boolean));

        // Step indicator
        this.stepIndicator = createStepProgress({
            steps: config.steps.map(s => s.label),
            currentStep: 0,
        });
        this.container.appendChild(this.stepIndicator.element);

        // Error area (between indicator and content)
        this.errorEl = createElement('div', { className: 'cs-wizard-error' });
        this.container.appendChild(this.errorEl);

        // Content area
        this.contentEl = createElement('div', { className: 'cs-wizard-content' });
        this.container.appendChild(this.contentEl);

        // Footer
        this.footerEl = createElement('div', { className: 'cs-wizard-footer' });
        this.buildFooter();
        this.container.appendChild(this.footerEl);

        // Render first step
        this.renderStep(0);
    }

    // ========================================================================
    // Public API (WizardAPI)
    // ========================================================================

    async next(): Promise<void> {
        if (this.disposed) return;

        const step = this.config.steps[this.currentIndex];

        // Validate
        if (step.validate) {
            const result = await step.validate();
            if (result === false) return;
            if (typeof result === 'string') {
                this.showError(result);
                return;
            }
        }

        this.clearError();

        if (this.currentIndex >= this.config.steps.length - 1) {
            // Last step — complete
            this.leaveCurrentStep();
            this.config.onComplete(this.state);
            return;
        }

        this.renderStep(this.currentIndex + 1);
    }

    back(): void {
        if (this.disposed || this.currentIndex <= 0) return;
        this.clearError();
        this.renderStep(this.currentIndex - 1);
    }

    goTo(stepId: string): void {
        if (this.disposed) return;
        const index = this.config.steps.findIndex(s => s.id === stepId);
        if (index === -1) return;
        this.clearError();
        this.renderStep(index);
    }

    getCurrentIndex(): number {
        return this.currentIndex;
    }

    getState(): Record<string, unknown> {
        return this.state;
    }

    setNextLabel(label: string): void {
        this.setButtonLabel(this.nextBtn, label);
    }

    setNextEnabled(enabled: boolean, reason?: string): void {
        this.nextBtn.disabled = !enabled;
        if (!enabled && reason) this.nextBtn.title = reason;
        else this.nextBtn.removeAttribute('title');
    }

    dispose(): void {
        if (this.disposed) return;
        this.disposed = true;
        this.leaveCurrentStep();
    }

    // ========================================================================
    // Internal
    // ========================================================================

    private renderStep(index: number): void {
        this.leaveCurrentStep();
        this.currentIndex = index;

        const step = this.config.steps[index];

        // Update step indicator
        this.stepIndicator.setCurrentStep(index);

        // Clear and render content. Next starts enabled: a gate belongs to the
        // step that set it, and the step about to render may set its own.
        clearElement(this.contentEl);
        this.stepDisposables = new DisposableStore();
        this.setNextEnabled(true);

        const ctx: WizardStepContext = {
            wizard: this,
            state: this.state,
            disposables: this.stepDisposables,
        };

        step.render(this.contentEl, ctx);

        // Update footer buttons
        this.updateFooter(step, index);
    }

    private leaveCurrentStep(): void {
        const step = this.config.steps[this.currentIndex];
        if (step && step.onLeave) {
            step.onLeave();
        }
        if (this.stepDisposables) {
            this.stepDisposables.dispose();
            this.stepDisposables = null;
        }
    }

    private buildFooter(): void {
        clearElement(this.footerEl);

        // Cancel button (left side)
        if (this.config.showCancel !== false && this.config.onCancel) {
            this.cancelBtn = createButton({
                label: 'Cancel',
                variant: 'default',
                onClick: () => this.config.onCancel?.(),
            });
            this.footerEl.appendChild(this.cancelBtn);
        }

        // Spacer
        const spacer = createElement('div', { className: 'cs-wizard-footer-spacer' });
        this.footerEl.appendChild(spacer);

        // Back button
        this.backBtn = createButton({
            label: 'Back',
            variant: 'default',
            onClick: () => this.back(),
        });
        this.footerEl.appendChild(this.backBtn);

        // Next button
        this.nextBtn = createButton({
            label: 'Next',
            variant: 'yes',
            onClick: () => this.next(),
        });
        this.footerEl.appendChild(this.nextBtn);
    }

    private updateFooter(step: WizardStep, index: number): void {
        const isFirst = index === 0;
        const isLast = index === this.config.steps.length - 1;

        // Back button
        if (step.hideBack || isFirst) {
            this.backBtn.style.display = 'none';
        } else {
            this.backBtn.style.display = '';
            // Find text node and update
            const backLabel = step.backLabel ?? 'Back';
            this.setButtonLabel(this.backBtn, backLabel);
        }

        // Cancel button
        if (this.cancelBtn) this.setButtonLabel(this.cancelBtn, step.cancelLabel ?? 'Cancel');

        // Next button
        if (step.hideNext) {
            this.nextBtn.style.display = 'none';
        } else {
            this.nextBtn.style.display = '';
            const nextLabel = step.nextLabel ?? (isLast ? 'Finish' : 'Next');
            this.setButtonLabel(this.nextBtn, nextLabel);
        }
    }

    private setButtonLabel(btn: HTMLButtonElement, label: string): void {
        // createButton() wraps the label in a <span class="cs-btn-label"> (so
        // the label can be hidden via CSS for icon-only modes), so the label is
        // not a direct text-node child of the button. Update that span when
        // present; fall back to a direct text node for buttons created some
        // other way. Without the span branch, per-step nextLabel / backLabel
        // overrides (e.g. "Finish" on a wizard's last step) silently no-op.
        const labelSpan = btn.querySelector('.cs-btn-label');
        if (labelSpan) {
            labelSpan.textContent = label;
            return;
        }
        const textNode = Array.from(btn.childNodes).find(
            n => n.nodeType === Node.TEXT_NODE
        );
        if (textNode) {
            textNode.textContent = label;
        }
    }

    private showError(message: string): void {
        this.errorEl.textContent = message;
        this.errorEl.classList.add('cs-wizard-error--visible');
    }

    private clearError(): void {
        this.errorEl.textContent = '';
        this.errorEl.classList.remove('cs-wizard-error--visible');
    }
}
